import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { apiZedocPatientActivityClinicalEvents } from '../../../common/api/zedoc';

/**
 * Hook to fetch clinical event data for patient activities
 *
 * @param {String} recipientId - Patient recipient ID
 * @param {String} projectId - Project ID
 * @param {Array} activities - Array of activity objects with {eventId, clinicalEventsType}
 * @returns {Object} { clinicalEvents: { [eventId]: data }, loading, error }
 */
export const usePatientClinicalEvents = (
  recipientId,
  projectId,
  activities = [],
) => {
  const dispatch = useDispatch();
  const [clinicalEvents, setClinicalEvents] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchClinicalEvents = useCallback(() => {
    // Filter activities that have clinical events (eventId exists)
    const clinicalActivities = activities.filter(
      (activity) => activity.eventId && activity.clinicalEventsType,
    );

    if (clinicalActivities.length === 0 || !recipientId || !projectId) {
      setClinicalEvents({});
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Prepare activities for the API call
    const activitiesParam = clinicalActivities.map((activity) => ({
      eventId: activity.eventId,
      clinicalEventsType: activity.clinicalEventsType,
    }));

    // Call Meteor method via Redux dispatch
    dispatch(
      callMethod(apiZedocPatientActivityClinicalEvents, {
        recipientId,
        projectId,
        activities: activitiesParam,
      }),
    )
      .then((result) => {
        setClinicalEvents(result || {});
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching clinical events:', err);
        setError(err);
        setLoading(false);
      });
  }, [
    dispatch,
    recipientId,
    projectId,
    JSON.stringify(activities.map((a) => a.eventId).sort()),
  ]);

  useEffect(() => {
    fetchClinicalEvents();
  }, [fetchClinicalEvents]);

  return {
    clinicalEvents,
    loading,
    error,
  };
};

export default usePatientClinicalEvents;
